// Developer: Ankit Gupta
/**
 * Turn a picture a person chose into the small data: URL the server keeps.
 *
 * It is drawn onto a square canvas in the browser, so what is sent is at most a few dozen
 * kilobytes whatever was picked, and is always a plain raster image.
 */
import i18n from '@/i18n';

const LIMIT = 85_000; // bytes of image the server accepts, with a margin

function bytesOf(dataUrl: string): number {
  return Math.floor((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
}

export async function squarePicture(file: File, size: number): Promise<string> {
  if (!/^image\/(png|jpeg|webp|gif|bmp)$/.test(file.type)) throw new Error(i18n.t('chat:picture.chooseType'));
  const bitmap = await createImageBitmap(file).catch(() => { throw new Error(i18n.t('chat:picture.unreadable')); });
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(i18n.t('chat:picture.noCanvas'));
  // the middle square of the picture, scaled to fit
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  return canvasPicture(canvas);
}

/** A drawn picture as the data: URL the server keeps: PNG, or a JPEG small enough when the PNG is not. */
export function canvasPicture(canvas: HTMLCanvasElement): string {
  const png = canvas.toDataURL('image/png');
  if (bytesOf(png) <= LIMIT) return png;
  for (const q of [0.9, 0.8, 0.65, 0.5]) {
    const jpg = canvas.toDataURL('image/jpeg', q);
    if (bytesOf(jpg) <= LIMIT) return jpg;
  }
  throw new Error(i18n.t('chat:picture.tooDetailed'));
}
